import { ApiError, containersControllerActivatePartnerContainer } from '@artic/api-client';
import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import { Checkbox } from '@artic/ui/checkbox';
import { Skeleton } from '@artic/ui/skeleton';
import { Switch } from '@artic/ui/switch';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragDropVerticalIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { CannotHideDialog } from '../../../features/containers/cannot-hide-dialog';
import { ContainerArchiveStatusBadge } from '../../../features/containers/container-archive-status-badge';
import { CreateContainerSheet } from '../../../features/containers/create-container-sheet';
import {
  type ProductTypeColumn,
  ProductTypeMatrixCells,
  ProductTypeMatrixHeaderCells,
  toProductTypeColumns,
} from '../../../features/containers/product-type-matrix';
import { CONTAINERS_KEY, containerListQueryOptions } from '../../../features/containers/queries';
import type { ContainerListItem } from '../../../features/containers/types';
import { usePartnerId, usePartnerName } from '../../../features/inbox/use-partner-name';
import { PRODUCTS_KEY, allPartnerContainersQueryOptions } from '../../../features/products/queries';
import type { PartnerContainer } from '../../../features/products/types';

export const Route = createFileRoute('/products/containers/')({
  component: DfmaContainersIndex,
});

function DfmaContainersIndex() {
  const { t } = useTranslation('common');
  const partnerId = usePartnerId();
  const partnerName = usePartnerName();
  const queryClient = useQueryClient();

  const {
    data: allContainers,
    isLoading: loadingAll,
    isError: errorAll,
  } = useQuery({
    // Partner-scoped for every caller here (admins included): this page is
    // the partner's view, so it must show exactly what the partner can see.
    ...containerListQueryOptions(partnerId),
    enabled: !!partnerId,
  });
  const {
    data: partnerContainers,
    isLoading: loadingPartner,
    isError: errorPartner,
  } = useQuery(allPartnerContainersQueryOptions(partnerId));

  const partnerContainerById = useMemo(
    () => new Map((partnerContainers ?? []).map((p) => [p.container_id, p])),
    [partnerContainers],
  );

  // ENG-677: read-only product-type matrix columns. The product_type container
  // is required, so it's already in the partner containers list — no extra fetch.
  const productTypeColumns = useMemo(
    () =>
      toProductTypeColumns(
        (partnerContainers ?? []).find((c) => c.container_key === 'product_type'),
      ),
    [partnerContainers],
  );

  // Archived custom containers are pulled out of the active/inactive lists and
  // surfaced separately under the "Show Archived Custom Containers" toggle.
  const requiredContainers = useMemo(
    () =>
      (allContainers ?? [])
        .filter((c) => c.is_required && !c.archived_at)
        .slice()
        .sort((a, b) => a.container_sort_order - b.container_sort_order),
    [allContainers],
  );

  const sortableContainers = useMemo(
    () =>
      (allContainers ?? [])
        .filter(
          (c) =>
            !c.is_required &&
            !c.archived_at &&
            partnerContainerById.get(c.container_id)?.is_active === true,
        )
        .slice()
        .sort((a, b) => {
          const aOrder = partnerContainerById.get(a.container_id)?.display_order ?? 0;
          const bOrder = partnerContainerById.get(b.container_id)?.display_order ?? 0;
          return aOrder - bOrder;
        }),
    [allContainers, partnerContainerById],
  );

  const inactiveContainers = useMemo(
    () =>
      (allContainers ?? [])
        .filter(
          (c) =>
            !c.is_required &&
            !c.archived_at &&
            partnerContainerById.get(c.container_id)?.is_active !== true,
        )
        .slice()
        .sort((a, b) => a.container_sort_order - b.container_sort_order),
    [allContainers, partnerContainerById],
  );

  // Archived custom containers (containers.archived_at set) — hidden by default.
  const archivedContainers = useMemo(
    () =>
      (allContainers ?? [])
        .filter((c) => !!c.archived_at)
        .slice()
        .sort((a, b) => a.container_name.localeCompare(b.container_name)),
    [allContainers],
  );

  const sortableIds = useMemo(
    () => sortableContainers.map((c) => c.container_id),
    [sortableContainers],
  );

  // ENG-501 §6.2: a hide blocked by In Progress work orders surfaces this dialog.
  const [blockedHide, setBlockedHide] = useState<{ inProgressCount: number } | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const activateMutation = useMutation({
    mutationFn: async (params: {
      partner_id: string;
      container_id: string;
      is_active: boolean;
      display_order: number;
      use_for_label: boolean;
    }) => {
      await containersControllerActivatePartnerContainer(params);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_KEY, 'partner-containers'],
      });
      void queryClient.invalidateQueries({
        queryKey: [CONTAINERS_KEY],
      });
    },
    onError: (err) => {
      // The backend blocks hiding (is_active false) while In Progress work orders
      // reference the container, returning 409 { in_progress_count }. The Switch
      // is controlled by query data (not optimistic), so it simply stays on.
      if (err instanceof ApiError && err.status === 409) {
        const raw = err.bodyAsRecord?.['in_progress_count'];
        setBlockedHide({ inProgressCount: typeof raw === 'number' ? raw : 0 });
      }
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (
      changed: { container_id: string; display_order: number; use_for_label: boolean }[],
    ) => {
      if (!partnerId) throw new Error('partner required');
      await Promise.all(
        changed.map(({ container_id, display_order, use_for_label }) =>
          containersControllerActivatePartnerContainer({
            partner_id: partnerId,
            container_id,
            is_active: true,
            display_order,
            use_for_label,
          }),
        ),
      );
    },
    onMutate: async (changed) => {
      const { queryKey } = allPartnerContainersQueryOptions(partnerId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PartnerContainer[]>(queryKey);
      const orderMap = new Map(changed.map((c) => [c.container_id, c.display_order]));
      queryClient.setQueryData<PartnerContainer[]>(queryKey, (old) =>
        old?.map((c) =>
          orderMap.has(c.container_id)
            ? { ...c, display_order: orderMap.get(c.container_id) ?? c.display_order }
            : c,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        const { queryKey } = allPartnerContainersQueryOptions(partnerId);
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_KEY, 'partner-containers', partnerId],
      });
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });

  const handleToggle = (container: ContainerListItem, currentlyActive: boolean) => {
    if (!partnerId) return;
    const partnerEntry = partnerContainerById.get(container.container_id);
    activateMutation.mutate({
      partner_id: partnerId,
      container_id: container.container_id,
      is_active: !currentlyActive,
      display_order: currentlyActive
        ? (partnerEntry?.display_order ?? container.container_sort_order)
        : sortableContainers.length,
      use_for_label: partnerEntry?.use_for_label ?? false,
    });
  };

  const handleToggleUseForLabel = (container: ContainerListItem) => {
    if (!partnerId) return;
    const partnerEntry = partnerContainerById.get(container.container_id);
    const isCurrentlyActive = container.is_required || partnerEntry?.is_active === true;
    if (!isCurrentlyActive) return;
    activateMutation.mutate({
      partner_id: partnerId,
      container_id: container.container_id,
      is_active: true,
      display_order: partnerEntry?.display_order ?? container.container_sort_order,
      use_for_label: !(partnerEntry?.use_for_label ?? false),
    });
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableContainers.findIndex((c) => c.container_id === active.id);
    const newIndex = sortableContainers.findIndex((c) => c.container_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(sortableContainers, oldIndex, newIndex);
    const prevByIdx = new Map(sortableContainers.map((c, i) => [c.container_id, i]));
    const changed = moved
      .map((c, i) => ({
        container_id: c.container_id,
        display_order: i,
        use_for_label: partnerContainerById.get(c.container_id)?.use_for_label ?? false,
      }))
      .filter((n) => prevByIdx.get(n.container_id) !== n.display_order);
    if (changed.length === 0) return;
    reorderMutation.mutate(changed);
  };

  const isLoading = loadingAll || loadingPartner;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">{t('terms.containers')}</h1>
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (errorAll || errorPartner) {
    return (
      <div className="text-destructive py-10 text-center text-sm">
        Failed to load container configuration. Please try again.
      </div>
    );
  }

  const toggleDisabledFor = (container: ContainerListItem) =>
    activateMutation.isPending || container.is_required;

  return (
    <div className="flex flex-col gap-4">
      <CannotHideDialog
        open={blockedHide !== null}
        onOpenChange={(open) => {
          if (!open) setBlockedHide(null);
        }}
        {...(blockedHide ? { inProgressCount: blockedHide.inProgressCount } : {})}
      />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{t('terms.containers')}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Choose which containers are active for your products, drag the active rows to set
            display order, and add your own custom containers.
          </p>
        </div>
        {partnerId && (
          <CreateContainerSheet
            defaultOwnerPartnerId={partnerId}
            {...(partnerName ? { defaultOwnerPartnerName: partnerName } : {})}
            triggerLabel="New Custom Container"
            detailRoute="partner"
          />
        )}
      </div>

      {(allContainers ?? []).length === 0 ? (
        <div className="text-muted-foreground py-10 text-center text-sm">
          No containers in the library yet.
        </div>
      ) : (
        <>
          {archivedContainers.length > 0 && (
            <label className="text-muted-foreground flex w-fit items-center gap-2 text-sm">
              <Checkbox
                checked={showArchived}
                onCheckedChange={(checked) => {
                  setShowArchived(checked === true);
                }}
              />
              Show Archived Custom Containers
            </label>
          )}
          <div className="overflow-x-auto rounded-md border">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b text-left">
                      <th className="w-8 px-2 py-2" aria-label="Drag handle column" />
                      <th className="px-4 py-2 font-medium">Active</th>
                      <th className="px-4 py-2 font-medium">Use For Label</th>
                      <th className="px-4 py-2 font-medium">Container</th>
                      <th className="px-4 py-2 text-right font-medium">Options</th>
                      <th className="w-24 px-4 py-2 text-right font-medium">Order</th>
                      <th className="px-4 py-2 font-medium">Type</th>
                      <ProductTypeMatrixHeaderCells columns={productTypeColumns} />
                    </tr>
                  </thead>
                  <tbody>
                    {requiredContainers.map((container, i) => (
                      <StaticContainerRow
                        key={container.container_id}
                        container={container}
                        isActive
                        visualOrder={i + 1}
                        onToggle={() => {
                          handleToggle(container, true);
                        }}
                        toggleDisabled={toggleDisabledFor(container)}
                        useForLabel={
                          partnerContainerById.get(container.container_id)?.use_for_label ?? false
                        }
                        onToggleUseForLabel={() => {
                          handleToggleUseForLabel(container);
                        }}
                        productTypeColumns={productTypeColumns}
                      />
                    ))}
                    {sortableContainers.map((container, i) => (
                      <SortableContainerRow
                        key={container.container_id}
                        container={container}
                        visualOrder={requiredContainers.length + i + 1}
                        onToggle={() => {
                          handleToggle(container, true);
                        }}
                        toggleDisabled={toggleDisabledFor(container)}
                        useForLabel={
                          partnerContainerById.get(container.container_id)?.use_for_label ?? false
                        }
                        onToggleUseForLabel={() => {
                          handleToggleUseForLabel(container);
                        }}
                        archiveQueuedAt={
                          partnerContainerById.get(container.container_id)?.archive_queued_at ??
                          null
                        }
                        productTypeColumns={productTypeColumns}
                      />
                    ))}
                    {inactiveContainers.map((container) => (
                      <StaticContainerRow
                        key={container.container_id}
                        container={container}
                        isActive={false}
                        visualOrder={null}
                        onToggle={() => {
                          handleToggle(container, false);
                        }}
                        toggleDisabled={toggleDisabledFor(container)}
                        useForLabel={false}
                        onToggleUseForLabel={null}
                        productTypeColumns={productTypeColumns}
                      />
                    ))}
                    {showArchived &&
                      archivedContainers.map((container) => (
                        <StaticContainerRow
                          key={container.container_id}
                          container={container}
                          isActive={false}
                          visualOrder={null}
                          onToggle={() => {}}
                          toggleDisabled
                          useForLabel={
                            partnerContainerById.get(container.container_id)?.use_for_label ?? false
                          }
                          onToggleUseForLabel={null}
                          archived
                          archiveQueuedAt={
                            partnerContainerById.get(container.container_id)?.archive_queued_at ??
                            null
                          }
                          productTypeColumns={productTypeColumns}
                        />
                      ))}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        </>
      )}
    </div>
  );
}

interface ContainerRowCellsProps {
  container: ContainerListItem;
  isActive: boolean;
  visualOrder: number | null;
  onToggle: () => void;
  toggleDisabled: boolean;
  useForLabel: boolean;
  /** Null when the row cannot toggle the label flag (e.g. inactive containers). */
  onToggleUseForLabel: (() => void) | null;
  /** partner_containers.archive_queued_at — drives the "Archived — pending" badge. */
  archiveQueuedAt?: string | null;
  /** ENG-634: an archived container shows an "Archived" label in place of the Active toggle. */
  archived?: boolean;
  /** ENG-677: read-only product-type matrix columns appended to each row. */
  productTypeColumns: ProductTypeColumn[];
}

function ContainerRowCells({
  container,
  isActive,
  visualOrder,
  onToggle,
  toggleDisabled,
  useForLabel,
  onToggleUseForLabel,
  archiveQueuedAt,
  archived = false,
  productTypeColumns,
}: ContainerRowCellsProps) {
  const labelToggleDisabled = toggleDisabled || onToggleUseForLabel === null;
  return (
    <>
      <td className="px-4 py-2 align-middle">
        {archived ? (
          <ContainerArchiveStatusBadge
            archivedAt={container.archived_at}
            queuedAt={archiveQueuedAt ?? null}
          />
        ) : (
          <Switch
            checked={container.is_required || isActive}
            onCheckedChange={onToggle}
            disabled={toggleDisabled}
            {...(container.is_required
              ? { title: 'Required containers are always active and cannot be deactivated.' }
              : {})}
          />
        )}
      </td>
      <td className="px-4 py-2 align-middle">
        <Switch
          checked={useForLabel}
          onCheckedChange={() => onToggleUseForLabel?.()}
          disabled={labelToggleDisabled}
          {...(onToggleUseForLabel === null
            ? { title: 'Activate this container before using it as a label.' }
            : {
                title:
                  "When on, the product's selected option for this container is shown as a label pill on the product list.",
              })}
        />
      </td>
      <td className="px-4 py-2 align-middle">
        <Link
          to="/products/containers/$containerId"
          params={{ containerId: container.container_id }}
          className="hover:text-primary inline-flex items-baseline gap-2 transition-colors"
          title="Open container detail to manage options"
        >
          <span className="font-medium underline-offset-2 hover:underline">
            {container.container_name}
          </span>
          <span className="text-muted-foreground font-mono text-xs">{container.container_key}</span>
        </Link>
      </td>
      <td className="px-4 py-2 text-right align-middle tabular-nums">{container.options_count}</td>
      <td className="px-4 py-2 text-right align-middle tabular-nums">
        {visualOrder ?? <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-4 py-2 align-middle">
        <div className="flex gap-1">
          <Badge variant={container.is_standard ? 'default' : 'secondary'}>
            {container.is_standard ? 'Standard' : 'Custom'}
          </Badge>
          {container.is_required && <Badge variant="outline">Required</Badge>}
        </div>
      </td>
      {/* ENG-677: read-only product-type matrix (green/grey/yellow). */}
      <ProductTypeMatrixCells container={container} columns={productTypeColumns} />
    </>
  );
}

interface SortableContainerRowProps {
  container: ContainerListItem;
  visualOrder: number;
  onToggle: () => void;
  toggleDisabled: boolean;
  useForLabel: boolean;
  onToggleUseForLabel: () => void;
  archiveQueuedAt?: string | null;
  productTypeColumns: ProductTypeColumn[];
}

function SortableContainerRow({
  container,
  visualOrder,
  onToggle,
  toggleDisabled,
  useForLabel,
  onToggleUseForLabel,
  archiveQueuedAt,
  productTypeColumns,
}: SortableContainerRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: container.container_id,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : 0,
  };
  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="hover:bg-muted/30 border-b transition-colors"
    >
      <td className="w-8 px-2 py-2 align-middle">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Drag to reorder ${container.container_name}`}
          className="text-muted-foreground hover:text-foreground cursor-grab touch-none active:cursor-grabbing"
          {...listeners}
        >
          <HugeiconsIcon icon={DragDropVerticalIcon} size={16} strokeWidth={2} />
        </Button>
      </td>
      <ContainerRowCells
        container={container}
        isActive
        visualOrder={visualOrder}
        onToggle={onToggle}
        toggleDisabled={toggleDisabled}
        useForLabel={useForLabel}
        onToggleUseForLabel={onToggleUseForLabel}
        archiveQueuedAt={archiveQueuedAt ?? null}
        productTypeColumns={productTypeColumns}
      />
    </tr>
  );
}

interface StaticContainerRowProps {
  container: ContainerListItem;
  isActive: boolean;
  visualOrder: number | null;
  onToggle: () => void;
  toggleDisabled: boolean;
  useForLabel: boolean;
  /** Null when the row's label flag cannot be toggled (inactive container). */
  onToggleUseForLabel: (() => void) | null;
  /** ENG-634: render the "Archived" label instead of the Active toggle. */
  archived?: boolean;
  archiveQueuedAt?: string | null;
  productTypeColumns: ProductTypeColumn[];
}

function StaticContainerRow({
  container,
  isActive,
  visualOrder,
  onToggle,
  toggleDisabled,
  useForLabel,
  onToggleUseForLabel,
  archived = false,
  archiveQueuedAt = null,
  productTypeColumns,
}: StaticContainerRowProps) {
  return (
    <tr className="hover:bg-muted/30 border-b transition-colors">
      <td className="w-8 px-2 py-2" />
      <ContainerRowCells
        container={container}
        isActive={isActive}
        visualOrder={visualOrder}
        onToggle={onToggle}
        toggleDisabled={toggleDisabled}
        useForLabel={useForLabel}
        onToggleUseForLabel={onToggleUseForLabel}
        archived={archived}
        archiveQueuedAt={archiveQueuedAt}
        productTypeColumns={productTypeColumns}
      />
    </tr>
  );
}
